import { type ReactNode, useContext } from 'react';
import { createContext } from 'react';
import { Navigate } from 'react-router';
import type { LessonsContextValue } from './interfaces';
import {
  useIndexCourseLessons,
  useSetCurrentCourseLesson,
} from '@/data/eduplay/courses';
import type { Lesson } from '@/data/eduplay/interfaces';
import {
  useShowLessonVideo,
  useUpdateLessonProgress,
} from '@/data/eduplay/lessons';

const LessonsContext = createContext<LessonsContextValue>({
  courseName: null,
  lessons: null,
  isPendingLessonVideo: false,
  isLoadingCourseLessons: false,
  error: null,
  currentLessonId: null,
  lessonVideo: null,
  courseId: null,
  updateLessonProgress: null,
  handleUpdateLessonProgress: null,
  setCurrentCourseLesson: null,
  hasFiles: null,
  isCourseComplete: false,
  isCompleted: null,
  courseProgress: 0,
});

const getNextLessonId = (lessons: Lesson[], currentLessonId: string) => {
  const currentLessonIndex = lessons?.findIndex(
    (lesson) => lesson.id === currentLessonId
  );
  return lessons?.[currentLessonIndex + 1]?.id;
};

const checkCourseCompletion = (lessons: Lesson[]) => {
  if (!lessons || lessons.length === 0) {
    return { isCourseComplete: false, courseProgress: 0 };
  }

  const completedLessons = lessons.filter(
    (lesson) => lesson.aula_alunos[0]?.is_completed
  ).length;

  const courseProgress = Math.ceil((completedLessons / lessons.length) * 100);
  const isCourseComplete = courseProgress === 100;

  return { isCourseComplete, courseProgress };
};

export const LessonsProvider = ({
  children,
  courseId,
}: {
  children: ReactNode;
  courseId: number;
}) => {
  const {
    data: lessons,
    isLoading: isLoadingCourseLessons,
    error,
  } = useIndexCourseLessons(courseId);
  const currentLessonId =
    lessons?.curso_alunos[0].aula_atual ?? lessons?.aulas[0]?.id;

  const { data: lessonVideo, isPending: isPendingLessonVideo } =
    useShowLessonVideo(currentLessonId ?? '');

  const { mutate: setCurrentCourseLesson } =
    useSetCurrentCourseLesson(courseId);

  const nextLessonId = getNextLessonId(
    lessons?.aulas ?? [],
    currentLessonId ?? ''
  );

  const { mutate: updateLessonProgress } = useUpdateLessonProgress(
    currentLessonId ?? ''
  );

  const { isCourseComplete, courseProgress } = checkCourseCompletion(
    lessons?.aulas ?? []
  );

  const isCompleted = lessons?.curso_alunos?.[0]?.is_completed;

  const handleUpdateLessonProgress = (
    progress: number,
    cursosHorariosId: number
  ) => {
    updateLessonProgress({ progress, cursosHorariosId });
    if (progress >= 95) {
      setCurrentCourseLesson(nextLessonId);
    }
  };

  const lessonVideoUrl = lessonVideo?.url ?? null;

  const value: LessonsContextValue = {
    courseId,
    courseName: lessons?.curso_detalhe?.curso?.curso ?? null,
    lessons: lessons?.aulas ?? null,
    isPendingLessonVideo,
    isLoadingCourseLessons,
    error: error ?? null,
    currentLessonId: currentLessonId ?? null,
    lessonVideo: lessonVideoUrl,
    updateLessonProgress,
    handleUpdateLessonProgress,
    setCurrentCourseLesson,
    hasFiles: lessons?.has_files ?? null,
    isCourseComplete,
    isCompleted: isCompleted ?? null,
    courseProgress,
  };

  if (error) return <Navigate to="/cursos/curso-nao-encontrado" replace />;

  return (
    <LessonsContext.Provider value={value}>{children}</LessonsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLessons = () => {
  const context = useContext(LessonsContext);
  if (!context)
    throw new Error('useLessons must be used within LessonsProvider');
  return context;
};
